import { useParams } from 'react-router';
import { DataTable } from '../components/DataTable.tsx';
import { ErrorState, KeyValueCard, LoadingState, StatusChip } from '../components/FdPrimitives.tsx';
import { useFetch } from '../hooks/useFetch.ts';

type SessionEvent = {
  event_id: string;
  device_alias: string;
  site_key: string;
  ip: string;
  event_name: string;
  ts: string;
  server_ts: string;
};

type SessionDetail = {
  session_id: string;
  device_alias: string;
  site_key: string;
  first_seen: string;
  last_seen: string;
  event_count: number;
  events: SessionEvent[];
};

export function SessionDetailPage() {
  const { id } = useParams();
  const state = useFetch<{ session: SessionDetail }>(`/sessions/${encodeURIComponent(id || '')}/events`);

  if (state.status === 'loading') return <LoadingState label="Loading session" />;
  if (state.status === 'error') return <ErrorState message={state.message} />;

  const { session } = state.data;

  return (
    <div className="page-stack">
      <div className="page-head">
        <div>
          <h2>Session {session.session_id || id}</h2>
          <p>{session.event_count.toLocaleString()} browser events in this session.</p>
        </div>
      </div>
      <KeyValueCard
        icon="timeline"
        title="Session facts"
        rows={[
          ['session_id', <span className="cid">{session.session_id}</span>],
          ['device_alias', <span className="cid">{session.device_alias}</span>],
          ['site_key', session.site_key],
          ['first_seen', session.first_seen],
          ['last_seen', session.last_seen],
          ['event_count', session.event_count.toLocaleString()],
        ]}
      />
      <DataTable
        title="Events"
        icon="timeline"
        rows={session.events}
        emptyTitle="No session events"
        emptyMessage="Events tied to this session id will appear here."
        columns={[
          { key: 'event_id', header: 'Event', className: 'cid' },
          { key: 'device_alias', header: 'Device', className: 'cid' },
          { key: 'event_name', header: 'Name', render: (event) => <StatusChip value={event.event_name} tone="new" /> },
          { key: 'site_key', header: 'Site key' },
          { key: 'ip', header: 'IP' },
          { key: 'ts', header: 'Client time' },
          { key: 'server_ts', header: 'Server time' },
        ]}
      />
    </div>
  );
}
